import { useFlag, useNewCode } from '@calendly/feature-check-react'
import { useQueryParams } from '@calendly/hooks'
import { buildQuery } from '@calendly/http-tools'
import { useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useHistory } from 'react-router-dom'

import { rescheduleInvitee } from 'actions/scheduled_events/rescheduling'
import { openSendPopupForEventRescheduling, openSendPopupForGroupEventRescheduling } from 'features/send_from_calendly'
import { TRACKING_EVENT_NAMES } from 'hooks/analytics_events/constants'
import { useTrack } from 'hooks/analytics_events/useTrack'

import { HostPickerPopup } from './popups/host_picker_popup'
import { SelectRescheduleMethodPopup } from './popups/select_reschedule_method_popup'
import { isHostRescheduleEnabledSelector, isShareOrReschedulePopupOpenSelector, isHostPickerPopupOpenSelector } from './selectors'
import {
  closePopup,
  goBack,
  openShareOrReschedulePopup,
  openHostPickerPopup,
} from './slice'


const isHostPickerPopupAvailable = (event) => {
  const allowSelectedHostRescheduling = event.showReschedulingModal

  const hasEnabledUserMemberships = event.eventType.memberships.some(
    ({ enabled, isPending }) => enabled && !isPending
  )

  return hasEnabledUserMemberships && allowSelectedHostRescheduling
}


const PopupOpenerFromURL = ({ event }) => {
  const dispatch = useDispatch()
  const history = useHistory()
  const queryParams = useQueryParams()
  const startReschedulingOnMount = queryParams.trigger_reschedule === event.uuid

  useEffect(() => {
    if (startReschedulingOnMount && isHostPickerPopupAvailable(event)) {
      dispatch(openHostPickerPopup())

      const { trigger_reschedule: _, ...modifiedParams } = queryParams
      history.replace({ search: buildQuery(modifiedParams) })
    }
  }, [startReschedulingOnMount, queryParams, history, dispatch, event])

  return null
}


export const HostReschedule = ({ event, invitee, isInviteeOnlyReschedule = false, Button, children, sendAnalytics }) => {
  const dispatch = useDispatch()
  const track = useTrack()
  const isShareOrReschedulePopupOpen = useSelector(isShareOrReschedulePopupOpenSelector)
  const isHostPickerPopupOpen = useSelector(isHostPickerPopupOpenSelector)
  const meetingIntelligenceEnabled = useNewCode('meeting_intelligence')?.enabled
  const hasNewMeetingsPage = useFlag('sa_new_meetings_page', false)
  const isNewMeetingsPageEnabled = hasNewMeetingsPage || meetingIntelligenceEnabled
  const { eventType } = event
  const isHostRescheduleRequestFlag = useFlag('host_reschedule_invitee_experience')
  const isHostRescheduleEnabled = useSelector(isHostRescheduleEnabledSelector)
  const allowShare = isHostRescheduleEnabled && isHostRescheduleRequestFlag

  const isOneOnOneEventType = eventType.kind === 'solo' && !eventType.poolingType

  useEffect(() => {
    if (isShareOrReschedulePopupOpen && isHostRescheduleRequestFlag && isNewMeetingsPageEnabled) {
      track({
        name: TRACKING_EVENT_NAMES.FLOW_STARTED,
        properties: {
          flow_name: 'host_reschedule_request',
          flow_location: isOneOnOneEventType ? 'event_details' : 'invitee_details',
          flow_version: 'v1',
          event_type_id: eventType?.id
        }
      })
    }
  }, [isShareOrReschedulePopupOpen, eventType, isOneOnOneEventType, isHostRescheduleRequestFlag, isNewMeetingsPageEnabled, track])


  if (!isHostRescheduleRequestFlag) {
    return children
  }

  if (!isNewMeetingsPageEnabled && !isOneOnOneEventType) {
    return children
  }

  const onClick = () => {
    if (sendAnalytics) sendAnalytics('reschedule')
    if (allowShare) {
      dispatch(openShareOrReschedulePopup())
      return
    }

    if (isHostPickerPopupAvailable(event)) {
      dispatch(openHostPickerPopup())
    } else {
      dispatch(rescheduleInvitee(invitee.uuid, null))
    }
  }

  const trackRescheduleNow = () => {
    track({
      name: TRACKING_EVENT_NAMES.FLOW_STARTED,
      properties: {
        flow_name: 'reschedule_for_invitee',
        flow_location: isOneOnOneEventType ? 'event_details' : 'invitee_details',
        flow_version: 'v1',
      }
    })
  }

  return (
    <>
      <PopupOpenerFromURL event={event} />
      <Button onClick={onClick} />
      {isShareOrReschedulePopupOpen && (
        <SelectRescheduleMethodPopup
          eventType={eventType}
          invitee={invitee}
          isInviteeOnlyReschedule={isInviteeOnlyReschedule}
          isOpen={isShareOrReschedulePopupOpen}
          onClose={() => dispatch(closePopup())}
          onShare={() => {
            dispatch(closePopup())
            if (isOneOnOneEventType) {
              dispatch(openSendPopupForEventRescheduling({
                event, invitee, isInviteeOnlyReschedule
              }))
            } else {
              dispatch(openSendPopupForGroupEventRescheduling({
                event, invitee, isInviteeOnlyReschedule
              }))
            }
            track({
              name: TRACKING_EVENT_NAMES.SEND_VIA_CALENDLY,
              properties: {
                event_type_id: eventType?.id,
                is_reschedule_request: true,
                step: 'open_modal'
              }
            })
          }}
          onReschedule={() => {
            if (isHostPickerPopupAvailable(event)) {
              dispatch(openHostPickerPopup())
            } else {
              dispatch(closePopup())
              dispatch(rescheduleInvitee(invitee.uuid, null, 'schedule_in_real_time', 'reschedule'))
            }
            trackRescheduleNow()
          }}
        />
      )}
      {isHostPickerPopupOpen && (
        <HostPickerPopup
          eventType={eventType}
          invitee={invitee}
          event={event}
          isInviteeOnlyReschedule={isInviteeOnlyReschedule}
          isOpen={isHostPickerPopupOpen}
          onClose={() => dispatch(closePopup())}
          onReschedule={() => {
            dispatch(closePopup())
            dispatch(rescheduleInvitee(invitee.uuid, null))
          }}
          onRescheduleWithHost={(hostUUID) => {
            dispatch(closePopup())
            dispatch(rescheduleInvitee(invitee.uuid, hostUUID))
          }}
          onBack={
            allowShare
              ? () => dispatch(goBack())
              : null
          }
        />
      )}
    </>
  )
}
